import { FX_DEFAULT } from "@/components/editor/ClipPanel";
import { layout, splitAt, reorder } from "@/lib/layout";
import { audOf } from "@/lib/clipAudio";
import { BUSES } from "@/lib/audioEngine";
import { mergeGrade } from "@/lib/grade";

const r3 = (v) => +(+v).toFixed(3);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, +v));
const TRANS = ["none", "dissolve", "dip", "wipeleft", "wiperight", "slideleft", "slideright", "flash", "leak", "flare", "starburst"];
const GRADE_NUM = { exposure: [-2, 2], contrast: [-1, 1], saturation: [0, 2], temperature: [-1, 1], tint: [-1, 1], shadows: [-1, 1], highlights: [-1, 1], depth: [0, 1], density: [0, 1], mix: [0, 1] };
const BUS_NUM = { gain: [-60, 12], low: [-12, 12], mid: [-12, 12], high: [-12, 12], threshold: [-60, 0], ratio: [1, 20], reverb: [0, 1], pan: [-1, 1] };
const trackFor = (kind) => (["footage", "animation", "export"].includes(kind) ? "video" : kind === "vfx" ? "fx" : kind === "mixdown" ? "audio" : kind);

const pickNums = (src, spec) => Object.fromEntries(Object.entries(spec).filter(([k]) => src[k] != null).map(([k, [lo, hi]]) => [k, clamp(src[k], lo, hi)]));
const wheel = (w = {}) => ({ x: clamp(w.x ?? 0, -1, 1), y: clamp(w.y ?? 0, -1, 1), l: clamp(w.l ?? 0, -1, 1) });

export function runActions(start, actions, ctx) {
  let { clips, grade, mixer } = start;
  const log = [], jobs = [];
  let seek = null;
  const laid = () => layout(clips, ctx.fullDuration);
  const find = (id) => laid().find((c) => c.id === id);
  const patch = (id, p) => { clips = clips.map((c) => (c.id === id ? { ...c, ...p } : c)); };

  for (const a of actions) {
    const c = a.clip_id ? find(a.clip_id) : null;
    if (a.clip_id && !c) { log.push({ ok: false, text: `Skipped ${a.type}: clip not found` }); continue; }
    try {
      switch (a.type) {
        case "add_clip": {
          const asset = ctx.assets.find((x) => x.id === a.asset_id);
          if (!asset) throw new Error("media not found");
          const track = a.as_layer ? "fx" : trackFor(asset.kind);
          const clip = { id: crypto.randomUUID(), asset_id: asset.id, track, start: track === "video" ? 0 : r3(a.start ?? ctx.time) };
          if (track === "fx") clip.fx = { ...FX_DEFAULT, blend: a.as_layer && asset.kind !== "vfx" ? "chroma" : asset.meta?.blend || "screen" };
          if (a.in != null) clip.in = r3(a.in);
          if (a.out != null) clip.out = r3(a.out);
          clips = [...clips, clip];
          if (track === "video" && a.index != null) clips = reorder(clips, clip.id, a.index);
          log.push({ ok: true, text: `Added ${asset.name} to ${track === "video" ? "V1" : track === "fx" ? "V2" : track}` });
          break;
        }
        case "blade": {
          const t = +a.at;
          if (!(t > c.start + 0.05 && t < c.start + c.len - 0.05)) throw new Error("cut point outside the clip");
          clips = splitAt(clips, c, t);
          log.push({ ok: true, text: `Bladed clip at ${t.toFixed(2)}s` });
          break;
        }
        case "trim": {
          const p = {};
          if (a.in != null) p.in = r3(Math.max(0, a.in));
          if (a.out != null) p.out = r3(a.out);
          if ((p.in ?? c.in) >= (p.out ?? c.out) - 0.05) throw new Error("in point must be before out point");
          patch(c.id, p);
          log.push({ ok: true, text: `Trimmed clip to ${(p.in ?? c.in).toFixed(2)}–${(p.out ?? c.out).toFixed(2)}s of the source` });
          break;
        }
        case "delete_clip": clips = clips.filter((x) => x.id !== c.id); log.push({ ok: true, text: "Removed a clip" }); break;
        case "move_clip":
          if (c.track === "video") { clips = reorder(clips, c.id, +a.index); log.push({ ok: true, text: `Moved clip to position ${+a.index + 1}` }); }
          else { patch(c.id, { start: r3(Math.max(0, a.start)) }); log.push({ ok: true, text: `Moved clip to ${(+a.start).toFixed(2)}s` }); }
          break;
        case "set_transition": {
          if (c.track !== "video" || c.idx === 0) throw new Error("transitions go on a later V1 clip");
          const type = TRANS.includes(a.transition) ? a.transition : "dissolve";
          patch(c.id, { trans: { type, dur: clamp(a.duration ?? 1, 0.1, 4) } });
          log.push({ ok: true, text: `Set ${type} transition` });
          break;
        }
        case "set_grade": {
          const g = { ...grade, ...pickNums(a, GRADE_NUM) };
          ["lift", "gamma", "gain"].forEach((w) => { if (a[w]) g[w] = wheel({ ...grade[w], ...a[w] }); });
          grade = g;
          log.push({ ok: true, text: "Adjusted the colour grade" });
          break;
        }
        case "reset_grade": grade = mergeGrade(); log.push({ ok: true, text: "Reset the grade" }); break;
        case "set_bus": {
          if (a.bus === "master") { mixer = { ...mixer, master: clamp(a.gain ?? mixer.master, -60, 12) }; log.push({ ok: true, text: "Set master level" }); break; }
          if (!BUSES.find((b) => b.id === a.bus)) throw new Error("unknown bus");
          const b = { ...mixer.buses[a.bus], ...pickNums(a, BUS_NUM), ...(a.mute != null && { mute: !!a.mute }), ...(a.solo != null && { solo: !!a.solo }) };
          mixer = { ...mixer, buses: { ...mixer.buses, [a.bus]: b } };
          log.push({ ok: true, text: `Adjusted ${BUSES.find((x) => x.id === a.bus).name}` });
          break;
        }
        case "set_ducking":
          mixer = { ...mixer, duck: { ...mixer.duck, ...(a.on != null && { on: !!a.on && mixer.duck.regions.length > 0 }), ...(a.depth != null && { depth: clamp(a.depth, -24, 0) }) } };
          log.push({ ok: true, text: mixer.duck.regions.length ? "Updated ducking" : "Ducking needs an Auto Mix analysis first — queued one" });
          if (!mixer.duck.regions.length) jobs.push({ type: "automix", style: null });
          break;
        case "auto_mix": jobs.push({ type: "automix", style: a.style || null }); log.push({ ok: true, text: "Running AI Auto Mix…" }); break;
        case "clip_audio": {
          if (c.track !== "video") throw new Error("clip audio is for V1 clips");
          const cur = audOf(c);
          const next = { ...cur, ...(a.gain != null && { gain: clamp(a.gain, -60, 12) }), ...(a.fade_in != null && { fade_in: clamp(a.fade_in, 0, 10) }),
            ...(a.fade_out != null && { fade_out: clamp(a.fade_out, 0, 10) }), ...(a.mute != null && { mute: !!a.mute }) };
          if (Array.isArray(a.points)) next.pts = a.points.map((p) => ({ t: r3(clamp(p.t, 0, c.len)), db: +clamp(p.db, -60, 12).toFixed(1) })).sort((x, y) => x.t - y.t);
          patch(c.id, { aud: next });
          log.push({ ok: true, text: "Edited clip audio" });
          break;
        }
        case "set_layer": {
          if (c.track !== "fx") throw new Error("not a V2 layer");
          const f = { ...c.fx, ...pickNums(a, { opacity: [0, 1], x: [-1.5, 1.5], y: [-1.5, 1.5], scale: [0.05, 3], rot: [-180, 180], fade_in: [0, 10], fade_out: [0, 10] }) };
          if (["screen", "chroma", "normal"].includes(a.blend)) f.blend = a.blend;
          if (Array.isArray(a.keyframes)) f.kfs = a.keyframes.map((k) => ({ t: r3(clamp(k.t, 0, c.len)), x: clamp(k.x ?? f.x, -1.5, 1.5), y: clamp(k.y ?? f.y, -1.5, 1.5),
            scale: clamp(k.scale ?? f.scale, 0.05, 3), rot: clamp(k.rot ?? f.rot, -180, 180), opacity: clamp(k.opacity ?? f.opacity, 0, 1),
            ease: ["linear", "in", "out", "inout"].includes(k.ease) ? k.ease : "linear" })).sort((x, y) => x.t - y.t);
          patch(c.id, { fx: f });
          log.push({ ok: true, text: "Adjusted the layer" });
          break;
        }
        case "add_light":
          jobs.push({ type: "light", preset: a.preset, duration: [4, 6, 10].includes(a.duration) ? a.duration : 6, start: r3(a.start ?? ctx.time) });
          log.push({ ok: true, text: `Creating a ${a.preset} light overlay…` });
          break;
        case "seek": seek = clamp(a.t, 0, 6 * 3600); log.push({ ok: true, text: `Moved the playhead to ${seek.toFixed(2)}s` }); break;
        default: log.push({ ok: false, text: `Unknown action ${a.type}` });
      }
    } catch (e) { log.push({ ok: false, text: `Skipped ${a.type}: ${e.message}` }); }
  }
  return { clips, grade, mixer, log, jobs, seek };
}

export function projectState({ laid, assets, grade, mixer, time, duration }) {
  const name = Object.fromEntries(assets.map((a) => [a.id, a.name]));
  return {
    playhead: r3(time), duration: r3(duration),
    media: assets.filter((a) => a.status === "ready" && a.kind !== "mixdown").map((a) => ({ id: a.id, name: a.name, kind: a.kind, type: a.media_type, duration: a.duration })),
    clips: laid.map((c) => ({ id: c.id, track: c.track, media: name[c.asset_id], start: r3(c.start), length: r3(c.len), source_in: r3(c.in), source_out: r3(c.out),
      ...(c.track === "video" && { position: c.idx, transition: c.trans?.type && c.trans.type !== "none" ? c.trans : undefined, audio: c.aud }),
      ...(c.track === "fx" && { layer: { blend: c.fx.blend, opacity: c.fx.opacity, x: c.fx.x, y: c.fx.y, scale: c.fx.scale, rot: c.fx.rot, keyframes: c.fx.kfs?.length || 0 } }) })),
    grade: Object.fromEntries(Object.entries(grade).filter(([k]) => k in GRADE_NUM || ["lift", "gamma", "gain"].includes(k))),
    mixer: { master: mixer.master, ducking: { on: mixer.duck.on, depth: mixer.duck.depth, regions: mixer.duck.regions.length },
      buses: Object.fromEntries(BUSES.map((b) => [b.id, pickNums(mixer.buses[b.id], BUS_NUM)])) },
  };
}
